import { Controller } from "@hotwired/stimulus"
import { loadMembers, filterMembers, renderMemberRow } from "../lib/mention_suggestion"
import { createSuggestionRenderer } from "../lib/suggestion_popup"

// FD-625: the workspace-member picker on the asks table's "Owner" cell.
//
// FD-615 shipped this column as a native <input list> against one page-level <datalist>, which
// renders EVERY workspace member as one unfiltered list — at FD-486's 487-member workspace that
// is a phone book. This replaces it with the picker the "@" mention feature already uses, and it
// does that by IMPORTING that feature's parts rather than rebuilding them:
//
//   loadMembers / filterMembers / renderMemberRow  <- lib/mention_suggestion (FD-621)
//   createSuggestionRenderer                       <- lib/suggestion_popup  (FD-209/FD-621)
//
// So there is ONE member cache, ONE search behaviour and ONE popup shell across the block
// editor's "@" menu, the Blocker/notes field (FD-622) and this. loadMembers is cached per URL and
// the URL is emitted once per page, so a 30-row asks table makes exactly ONE request between all
// of its cells — shared with any composer on the same page.
//
// TWO SEMANTICS DIFFER from mention_autocomplete_controller, and they are why this is a sibling
// controller rather than a mode flag on that one — threading a flag through both would put
// FD-622's "never dispatch change" rule and this file's "must dispatch change" rule three lines
// apart in the same method:
//
//   1. THE QUERY IS THE WHOLE VALUE. A mention is an "@…" slice inside a sentence, found by
//      caret arithmetic. An owner is the entire field, so there is no sentinel and no caret
//      maths — clicking an empty cell opens the list, and typing narrows it.
//
//   2. SELECTING IS THE COMMIT, so it must dispatch `change`. A mention deliberately does NOT
//      (see insert() below).
//
// That difference in semantics drives a third, which is really a consequence of the first: with
// no "@" sentinel the menu is open before the user has expressed ANY intent, so this controller
// has to be much stricter than its sibling about which keys the menu is allowed to claim — see
// handleKeyDown.
//
// The Stimulus wiring around the shared pieces (the per-focus members() memo, the activeRenderer()
// liveness check, the generation counter) is still close to its sibling's. Folding that into a
// shared factory in lib/ is a worthwhile follow-up; it is deliberately not done here, because it
// would mean rewriting FD-622's just-shipped controller in a PR whose diff two other tickets have
// to reconcile against.
//
// Free typing still wins over the picker in every case: owner_name is free text by design
// (FD-615 KTD2) because an ask is regularly owed by a vendor or someone outside the workspace,
// and this control only ever SUGGESTS.
export default class extends Controller {
  static targets = [ "display", "input" ]
  static values = { url: String }

  connect() {
    // Bound here rather than through data-action because this needs five of them and the input
    // already carries `change->auto-submit#submit`; merging six entries into one attribute is far
    // harder to read. (The display button's click IS a data-action — one entry, so it reads fine.)
    this.onFocus = () => { this.membersPromise = null; this.refresh() }
    this.onInput = () => this.refresh()
    this.onClick = () => this.refresh()
    this.onKeyDown = (event) => this.handleKeyDown(event)
    this.onBlur = () => this.conceal()

    const input = this.inputTarget
    input.addEventListener("focus", this.onFocus)
    input.addEventListener("input", this.onInput)
    input.addEventListener("click", this.onClick)
    input.addEventListener("keydown", this.onKeyDown)
    input.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    if (this.hasInputTarget) {
      const input = this.inputTarget
      input.removeEventListener("focus", this.onFocus)
      input.removeEventListener("input", this.onInput)
      input.removeEventListener("click", this.onClick)
      input.removeEventListener("keydown", this.onKeyDown)
      input.removeEventListener("blur", this.onBlur)
    }
    // conceal(), not just close(): every exit path resets the cell, the way
    // inline_edit_controller#stopEditing does. An ordinary morph never reaches here (idiomorph
    // skips a data-turbo-permanent node outright), but a row leaving the rendered set entirely —
    // the roll-up's Incomplete tab drops asks as they complete — removes the frozen cell with the
    // freeze still on it. Leaving that attribute behind on a detached node is harmless; leaving
    // the controller's idea of "open" behind is not.
    this.conceal()
  }

  // --- reveal / conceal --------------------------------------------------------------

  // The reveal/conceal pair only applies to a TABLE CELL, where the resting state is an avatar
  // (or a short string) and the input is what costs width. The "Add an ask" row mounts this
  // controller directly on an always-visible input with no display target — it is a form field
  // being filled in, not a display of stored state — so both halves no-op there.
  //
  // Revealing does not reflow the table: .fd-owner-input is overlaid, not inline.
  //
  // data-turbo-permanent is load-bearing and NOT decoration. Open/closed is carried by the
  // input's `hidden` attribute, and the server always renders it closed — so ANY morph would
  // re-apply `hidden` and slam an open cell shut. That is not hypothetical: the workflow this
  // column invites is filling Owner straight down the table, where each row's auto-submit
  // redirect_backs to the same url, i.e. a morph landing while the NEXT row is being typed into.
  // Freezing the cell while it is open is the same trick inline_edit_controller uses on the
  // description editor, and it needs the stable per-row id the cell already carries.
  reveal() {
    if (!this.hasDisplayTarget) return
    this.element.setAttribute("data-turbo-permanent", "")
    this.displayTarget.hidden = true
    this.inputTarget.hidden = false
    this.inputTarget.focus({ preventScroll: true }) // FD-1190: it opens over the cell just clicked
    // Select rather than place a caret: the common edit is "replace this owner", and an empty
    // field opens the unfiltered list either way.
    this.inputTarget.select()
  }

  // Back to resting. Reached on blur, which a popup gesture cannot trigger — the shell calls
  // preventDefault on mousedown anywhere inside itself precisely so clicking a row does not blur
  // the field and auto-submit the row mid-gesture.
  //
  // Dropping the freeze is what lets our OWN write morph back in — leave it on and the cell would
  // keep showing the input forever, never resolving into the avatar it just earned.
  conceal() {
    this.close()
    if (!this.hasDisplayTarget || !this.hasInputTarget) return
    this.element.removeAttribute("data-turbo-permanent")
    this.inputTarget.hidden = true
    this.displayTarget.hidden = false
  }

  // --- member list -------------------------------------------------------------------

  // ONE lookup per focus, not one per keystroke. loadMembers caches by URL but deliberately
  // EVICTS a failed fetch so an outage stays transient — which, called straight from refresh(),
  // would mean every keystroke re-fires the request. One 429 (the endpoint is throttled) or one
  // expired session then sustains itself, and each failure renders as "No matching people", which
  // reads as fact rather than breakage. Memoising per focus bounds it: the retry happens when the
  // user comes back to the cell.
  members() {
    return (this.membersPromise ||= loadMembers(this.urlValue))
  }

  // --- popup -------------------------------------------------------------------------

  // The ONLY way to ask "is the menu open?". Any row's auto-submit redirect_backs to the same
  // URL, i.e. a Turbo morph, which deletes the body-mounted popup while this controller and its
  // input survive in place — so a stored boolean would claim "open" about a menu the user watched
  // vanish, and refresh() would never rebuild it. Liveness is the shell's to know: ask it, and
  // drop the corpse.
  activeRenderer() {
    if (this.renderer && !this.renderer.isLive()) this.close()
    return this.renderer
  }

  refresh() {
    const query = this.inputTarget.value

    // Reap a morph-deleted popup BEFORE stamping the generation, never after. activeRenderer()
    // drops a dead renderer through close(), and close() bumps this.generation — so stamping
    // first meant the stamp was already one behind by the time the fetch resolved, stale() was
    // true immediately, onUpdate never ran, and the menu sat on "Loading…" (swallowing Enter,
    // Tab, the arrows and Escape) for the rest of that keystroke. The trigger is the exact
    // workflow this column invites: row 1's redirect_back morph landing while row 2 is typed into.
    const live = this.activeRenderer()

    // A generation counter, because members arrive asynchronously: without it a resolve from two
    // keystrokes ago repaints the popup with a stale list, or reopens one already dismissed.
    const generation = (this.generation = (this.generation || 0) + 1)
    const stale = () => generation !== this.generation

    if (!live) {
      this.renderer = createSuggestionRenderer({
        prefix: "fd-mention",
        emptyText: "No matching people",
        renderItem: renderMemberRow,
      })
      // loading:true with an empty list makes the shell render its loading row and swallow the
      // navigation keys, instead of claiming "no matches" and letting Enter through while the very
      // first fetch is still in flight.
      this.renderer.onStart(this.props([], true))
    }

    this.members().then((members) => {
      if (stale() || !this.activeRenderer()) return
      this.renderer.onUpdate(this.props(filterMembers(members, query), false))
    })
  }

  props(items, loading) {
    return {
      items,
      loading,
      command: (member) => this.insert(member),
      // `element` is what the shell mounts against — it resolves the nearest OPEN <dialog> from
      // here, so the picker works inside a modal, and mounts outside this cell either way so the
      // asks card's own overflow container cannot clip it. That is also why the popup costs the
      // table no width at all.
      element: this.inputTarget,
      clientRect: () => this.inputTarget.getBoundingClientRect(),
    }
  }

  close() {
    if (!this.renderer) return
    this.renderer.onExit()
    this.renderer = null
    this.generation = (this.generation || 0) + 1 // orphan any in-flight resolve
  }

  // --- selection ---------------------------------------------------------------------

  insert(member) {
    const input = this.inputTarget
    // The whole value, not a splice: this field IS the owner. `name` (not `token`) because what
    // gets stored is a human-readable owner, not something Mention.scan! has to match.
    input.value = member.name

    // Close BEFORE the write, then dispatch. FD-622's rule for the mention picker is the
    // opposite — never dispatch a synthetic `change` — but that rule is about a popup that is
    // still OPEN: requestSubmit -> redirect_back -> morph would delete it out from under the
    // user's hand mid-gesture. It does not transfer here, for two reasons:
    //
    //   * the popup is already gone by the time anything is dispatched, so there is no gesture
    //     left to race;
    //   * picking an owner IS the commit, where inserting a mention is mid-composition. Setting
    //     .value from script fires nothing at all, so without an explicit `change` the picked
    //     owner would silently never save.
    //
    // `input` goes first so field-sizing and any other input listener see the value; `change` is
    // what auto-submit#submit is bound to.
    this.close()
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))

    // Then back to resting, which also drops the data-turbo-permanent freeze — without that the
    // morph our own write is about to trigger would skip this cell, and it would sit showing the
    // input forever instead of resolving into the avatar it just earned.
    this.conceal()
  }

  // --- keys --------------------------------------------------------------------------

  handleKeyDown(event) {
    const renderer = this.activeRenderer()
    if (!renderer) return

    // TAB NEVER SELECTS. The shell maps Tab and Enter to the same choose(), which is right for an
    // editor but wrong here twice over: it would commit the highlighted row instead of moving on,
    // and on a no-match query it swallows Tab entirely so a free-typed vendor name could never be
    // committed from the keyboard at all. Tab means "commit what I typed and move on" — that IS
    // the fill-Owner-down-the-table workflow this column invites. Close and let it through: the
    // browser's own blur fires `change`, which is the normal auto-submit path.
    if (event.key === "Tab") return this.close()

    // AN UNFILTERED LIST IS NOT A SELECTION. Opening a cell shows every member with row 0
    // highlighted (filterMembers short-circuits an empty query to the whole list), so forwarding
    // Enter here would PATCH the alphabetically-first colleague onto an ask nobody chose — the
    // exact silent misattribution FD-615 kept this column out of the user_id business to avoid.
    // Narrowing the list is the act of choosing; until the user has typed something, Enter
    // belongs to the form, not to the menu.
    if (event.key === "Enter" && !this.inputTarget.value.trim()) return this.close()

    // The shell also refuses to act on a popup that is no longer connected, so a broadcast
    // refresh that swept the menu away can never select whoever was highlighted in a menu the
    // user watched disappear.
    if (!renderer.onKeyDown({ event })) return

    // preventDefault is OURS to do. @tiptap/suggestion called it for the editor; on a plain field
    // nothing does, and the defaults are destructive: Enter submits the enclosing form (on the
    // "Add an ask" row that creates an ask named after a half-typed query), Escape closes an
    // enclosing <dialog> or the peek drawer taking the half-written note with it, and the arrows
    // walk the caret out of the field.
    event.preventDefault()
    event.stopPropagation()
    if (event.key === "Escape") this.close()
  }
}
